import React from "react";
import { Box } from "@mui/material";
import classNames from "classnames";
import { IconRefresh } from "../../utils/icons";
import { markupSignature } from "../PdpPages/markupWatch";

export interface PdpUrlScore {
    id: number;
    overall_score: number | null;
    human_conversion_score: number | null;
    agentic_commerce_score: number | null;
    scored_at: string | null;
    // 'v1' model-written totals (before Oct 2026) | 'v2' code-summed totals
    scoring_version?: string | null;
}

// The row's most recent scoring attempt of ANY status — `pending` is the
// placeholder the API writes when it enqueues the on-add score (the monthly
// cron doesn't write one: its batch drains over an hour+, which would read
// as stalled here); it resolves in place to `ready` / `error`.
export interface PdpUrlAttempt {
    id: number;
    status: "pending" | "ready" | "error" | string;
    created_at: string | null;
    scored_at: string | null;
    error: string | null;
}

// A pending attempt older than this is treated as stalled (worker down /
// job lost) so the UI stops promising a result and re-enables Rescore.
export const PENDING_STALE_MS = 15 * 60 * 1000;

export const isAttemptPending = (attempt?: PdpUrlAttempt | null) => {
    if (!attempt || attempt.status !== "pending") return false;
    const started = attempt.created_at ? new Date(attempt.created_at).getTime() : 0;
    return Date.now() - started < PENDING_STALE_MS;
};

// A CLASS rather than an inline style so dark can take a token: these hues are
// tuned for a light chip and drop to ~2.8:1 on the dark one. Light keeps the
// exact original literals, so it is byte-identical.
export const scoreCls = (score: number | null) => {
    if (score == null) return "text-[#9B958C] dark:text-muted";
    if (score >= 70) return "text-[#1C7A5A] dark:text-up";
    if (score >= 40) return "text-[#A0662E] dark:text-accent-text";
    return "text-[#C0392B] dark:text-down";
};

const ScoreChip = ({ label, score }: { label: string; score: number | null }) => (
    <Box className="flex flex-col items-center px-3 py-[6px] rounded-[9px] bg-surface-2 border border-border-token min-w-[74px]">
        <span className={classNames("text-[16px] font-bold leading-[1.2]", scoreCls(score))}>
            {score == null ? "—" : score}
        </span>
        <span className="text-[10px] font-semibold text-muted uppercase tracking-[0.4px]">{label}</span>
    </Box>
);

interface Props {
    score?: PdpUrlScore | null;
    attempt?: PdpUrlAttempt | null;
    // Sync rescore is internal-only (proTeamControl); brand roles get the
    // async request-score handler instead — pass undefined to hide the
    // button entirely.
    onRescore?: () => void;
    rescoring?: boolean;
    // Another row on the same panel is rescoring — the sync endpoint runs
    // one audit at a time, so hold the button (async mode doesn't need it).
    rescoreLocked?: boolean;
    // Idle-state label override ("Run report" for the async brand path).
    // Failed attempts still read "Retry scoring".
    rescoreLabel?: string;
    className?: string;
}

// Score chips + a status line for one PDP URL row: last good score, an
// in-progress pulse while the async score runs, or the last failed attempt
// with its reason. Shared by the product settings and video settings panels.
export function PdpScoreStatus({
    score,
    attempt,
    onRescore,
    rescoring,
    rescoreLocked,
    rescoreLabel: rescoreLabelProp,
    className
}: Props) {
    const pending = isAttemptPending(attempt);
    const stalled = !!attempt && attempt.status === "pending" && !pending;
    // A failed attempt only matters if it's newer than the last good score.
    const failed =
        !!attempt &&
        attempt.status === "error" &&
        (!score || !score.scored_at || !attempt.created_at ||
            new Date(attempt.created_at).getTime() > new Date(score.scored_at).getTime());

    let statusText: React.ReactNode;
    let statusCls = "text-muted";
    if (pending) {
        statusText = "Scoring in progress… this usually takes a minute or two.";
        statusCls = "text-accent-text animate-pulse";
    } else if (stalled) {
        statusText = "Scoring is taking longer than expected — you can rescore now.";
        statusCls = "text-[#A0662E] dark:text-accent-text";
    } else if (failed) {
        statusText = `Last scoring attempt failed${attempt?.error ? ` (${attempt.error})` : ""}.${
            score ? " Showing the previous score." : ""
        }`;
        statusCls = "text-[#C0392B] dark:text-down";
    } else if (score && score.scored_at) {
        statusText = `Last scored ${new Date(score.scored_at).toLocaleDateString()}`;
    } else {
        statusText = "Not scored yet — the initial score runs within minutes of adding, then on a regular schedule.";
    }

    let rescoreLabel = rescoreLabelProp || "Rescore now";
    if (rescoring) rescoreLabel = "Rescoring… (can take a minute)";
    else if (failed) rescoreLabel = "Retry scoring";

    return (
        <Box className={classNames("flex flex-col gap-3", className)}>
            <Box className={classNames("flex items-center gap-[8px] transition-opacity", pending && "opacity-50")}>
                <ScoreChip label="Overall" score={score ? score.overall_score : null} />
                <ScoreChip label="Human" score={score ? score.human_conversion_score : null} />
                <ScoreChip label="Agentic" score={score ? score.agentic_commerce_score : null} />
            </Box>
            <Box className="flex items-center justify-between gap-[10px] flex-wrap">
                <p className={classNames("text-[11px] leading-[1.4]", statusCls)}>{statusText}</p>
                {onRescore && (
                    <button
                        type="button"
                        onClick={onRescore}
                        disabled={pending || rescoring || rescoreLocked}
                        className="inline-flex items-center gap-[5px] px-3 py-[6px] rounded-lg text-[12px] font-semibold text-accent-text bg-tint border border-[#EAD9B8] dark:border-border-token hover:bg-[#F7EDD9] dark:hover:bg-surface-2 transition-colors disabled:opacity-60"
                    >
                        {/* stroke is an SVG attribute and cannot read var(); inherit --accent-text via currentColor */}
                        <IconRefresh size={13} stroke="currentColor" />
                        {rescoreLabel}
                    </button>
                )}
            </Box>
        </Box>
    );
}

// Re-fetch on an interval while any row's score is still pending; stops on
// its own once nothing is pending (resolved, or stalled past
// PENDING_STALE_MS). Interval-based (not re-armed by data changes) so a
// refetch that fails — silently, by design of the callers — doesn't end
// polling; the latest `refetch` is read through a ref so the interval never
// calls a stale closure.
//
// Then it settles: the API rebuilds the page's markup (re-detected page
// videos, VideoObjects, the page markup) in the background AFTER the
// score lands, so polling carries on until the rows' markup changes or
// `settleMs` passes — an auto analysis (a URL just added) shows its JSON-LD
// without a manual refresh, as Analyze now does (watchMarkupChange).
export function usePendingScorePoll(
    rows: Array<{ latest_attempt?: PdpUrlAttempt | null }>,
    refetch: () => Promise<unknown>,
    intervalMs = 5000,
    settleMs = 90000
) {
    const anyPending = rows.some((r) => isAttemptPending(r.latest_attempt));
    const signature = rows.map((r) => markupSignature(r as any)).join("|");
    const refetchRef = React.useRef(refetch);
    refetchRef.current = refetch;
    const wasPending = React.useRef(anyPending);
    const [settle, setSettle] = React.useState<{ sig: string; until: number } | null>(null);
    React.useEffect(() => {
        if (wasPending.current && !anyPending) setSettle({ sig: signature, until: Date.now() + settleMs });
        if (anyPending) setSettle(null);
        wasPending.current = anyPending;
    }, [anyPending]);
    // The markup moved (or the window closed): done settling.
    const settling = !!settle && settle.sig === signature;
    React.useEffect(() => {
        if (settle && settle.sig !== signature) setSettle(null);
    }, [settle, signature]);
    React.useEffect(() => {
        if (!anyPending && !settling) return undefined;
        const timer = setInterval(() => {
            if (!anyPending && settle && Date.now() > settle.until) {
                setSettle(null);
                return;
            }
            refetchRef.current();
        }, intervalMs);
        return () => clearInterval(timer);
    }, [anyPending, settling, settle, intervalMs]);
}
